import { MbeCheck } from '@/components/icons';
import { Section } from '@/components/shared';
import { ROUTE_PATHS } from '@/constants';
import { useAppSelector, useAuth } from '@/hooks';
import { pathJoin, scrollToTop } from '@/utils';
import { Auth } from '@aws-amplify/auth';
import { Box, Divider, Flex, Heading, Icon, Text } from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { SimpleSignUpForm } from '../components/RequestedConfirmationStep';

export const RequestedConfirmationStep: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { authUser } = useAuth();
  const isLogged = authUser?.isValid();
  const userInfo = useAppSelector((state) => state.users.user);
  const navigate = useNavigate();

  scrollToTop();

  const userEmail: string | undefined = userInfo?.email;

  const handleSubmit = async ({ password }: { password: string }): Promise<void> => {
    if (userEmail) {
      try {
        await Auth.signUp({
          username: userEmail,
          password,
          autoSignIn: {
            enabled: false,
          },
        });

        navigate({
          pathname: pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.checkEmailMessage]),
          search: createSearchParams({
            name: userEmail,
          }).toString(),
        });
      } catch (error) {
        console.log('error signin up', error);
      }
    }
  };

  const signUpSection = !isLogged ? (
    <>
      <Divider borderColor="brand.gray" />
      <Flex
        alignItems={{ base: 'center', md: 'flex-start' }}
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: 4, md: 16 }}
        justifyContent="center"
        px={{ base: 8, lg: 28, xl: 36 }}
        w="full"
      >
        <Box textAlign={{ base: 'center', md: 'left' }}>
          <Heading fontSize={{ base: 'h3.base', md: 'h3.md' }} fontWeight="bold" pb={2}>
            {t('WIZARD.REQUESTED_CONFIRMATION.SIGNUP_TITLE')}
          </Heading>
          <Text fontSize="large" mb={8}>
            {t('WIZARD.REQUESTED_CONFIRMATION.SIGNUP_CONTENT')}
          </Text>
        </Box>
        <Flex direction="column" gap={4} minWidth={{ base: 'full', md: 250 }}>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <SimpleSignUpForm onSubmit={handleSubmit} />
        </Flex>
      </Flex>
    </>
  ) : undefined;

  return (
    <Section
      containerProps={{ py: 0, gap: { base: 8, md: 16 }, marginTop: { base: -10 } }}
      headingProps={{ mt: 0 }}
      id="requested-section"
    >
      <Flex alignItems="center" direction="column" gap={4} px={{ base: 0, md: 16, xl: 44 }}>
        <Icon as={MbeCheck} h={24} w={24} />
        <Text fontSize={{ base: '32px', md: 'h1.md' }} fontWeight="bold" textAlign="center">
          {t('WIZARD.REQUESTED_CONFIRMATION.TITLE')}
        </Text>
        <Text textAlign="center">
          <Trans i18nKey="WIZARD.REQUESTED_CONFIRMATION.CONTENT" />
        </Text>
      </Flex>
      {signUpSection}
    </Section>
  );
};
